// src/context/CartContext.jsx
// ─────────────────────────────────────────────────────────────────────────────
// Cart that works in £ GBP and $ USD.
//
// Each cart item stores BOTH prices (priceGBP + priceUSD) when it's added, so
// switching currency instantly re-prices the cart. Items with no $ price are
// flagged `unavailable` in USD and checkout is blocked until they're removed.
// The server re-checks every price from Firestore, so these are display only.
//
// The product page should call addToCart with:
//   { id, productId, variantKey, variantLabel, name, image, length,
//     priceGBP, priceUSD }
// (`id` must be unique per product + option, e.g. `${productId}__${variantKey}`)
//
// Must sit INSIDE <CurrencyProvider>.
// ─────────────────────────────────────────────────────────────────────────────
import React, { createContext, useState, useContext, useEffect, useMemo } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../services/firebase";
import { useCurrency } from "./CurrencyContext";

const CartContext = createContext();

export const useCart = () => {
    return useContext(CartContext);
};

const toPrice = (v) => {
    if (v === undefined || v === null || v === "") return null;
    const n = Number(v);
    return !isNaN(n) && n > 0 ? n : null;
};

// Older carts saved in localStorage only have `price` (GBP)
const normaliseStoredItem = (item) => ({
    ...item,
    priceGBP: toPrice(item.priceGBP) ?? toPrice(item.price),
    priceUSD: toPrice(item.priceUSD),
});

export const CartProvider = ({ children }) => {
    const { currency, formatPrice } = useCurrency();

    // 1. Initialize cart from localStorage
    const [cart, setCart] = useState(() => {
        try {
            const savedCart = localStorage.getItem("bm_cart");
            return savedCart ? JSON.parse(savedCart).map(normaliseStoredItem) : [];
        } catch (error) {
            console.error("Error reading cart from localStorage:", error);
            return [];
        }
    });

    const [appliedCode, setAppliedCode] = useState("");

    // Live promo rules from the database, per currency.
    // USD values are null until set in the admin, which makes the code UK-only.
    const [livePromoConfig, setLivePromoConfig] = useState({
        isActive: false,
        code: "",
        GBP: { discountAmount: 30, minimumSpend: 250 },
        USD: { discountAmount: null, minimumSpend: null },
    });

    useEffect(() => {
        const fetchPromoRules = async () => {
            try {
                const snap = await getDoc(doc(db, "site_config", "home_content"));
                if (snap.exists() && snap.data().announcementData) {
                    const data = snap.data().announcementData;
                    const usdDiscount = toPrice(data.discountAmountUSD);
                    const usdMinimum = data.minimumSpendUSD === undefined || data.minimumSpendUSD === null || data.minimumSpendUSD === ""
                        ? null
                        : Number(data.minimumSpendUSD);
                    setLivePromoConfig({
                        isActive: data.isActive,
                        code: data.highlightText ? data.highlightText.trim().toUpperCase() : "",
                        GBP: {
                            discountAmount: Number(data.discountAmount) || 30,
                            minimumSpend: Number(data.minimumSpend) || 250,
                        },
                        USD: {
                            discountAmount: usdDiscount,
                            minimumSpend: usdDiscount !== null && !isNaN(usdMinimum) ? usdMinimum : null,
                        },
                    });
                }
            } catch (err) {
                console.error("Failed to fetch promo rules", err);
            }
        };
        fetchPromoRules();
    }, []);

    // 2. Save to localStorage every time the cart changes
    useEffect(() => {
        try {
            localStorage.setItem("bm_cart", JSON.stringify(cart));
        } catch (error) {
            console.error("Error saving cart to localStorage:", error);
        }
    }, [cart]);

    // 3. Items priced in the CURRENT currency.
    //    `price` keeps its old meaning (unit price) so existing pages keep working.
    const pricedCart = useMemo(() => cart.map(item => {
        const unit = currency === "USD" ? item.priceUSD : item.priceGBP;
        return { ...item, price: unit ?? 0, unavailable: unit === null };
    }), [cart, currency]);

    const unavailableItems = pricedCart.filter(item => item.unavailable);
    const hasUnavailableItems = unavailableItems.length > 0;

    // Math Calculations (current currency, available items only)
    const cartSubtotal = pricedCart.reduce((total, item) => total + (item.unavailable ? 0 : item.price * item.quantity), 0);

    const promoRules = livePromoConfig[currency];
    const promoAvailableInCurrency = promoRules.discountAmount !== null && promoRules.minimumSpend !== null;
    const discount = appliedCode && promoAvailableInCurrency ? promoRules.discountAmount : 0;
    const cartTotal = Math.max(cartSubtotal - discount, 0);

    // SAFETY CHECK: Auto-remove code if cart drops below threshold, the admin turns the sale off,
    // or the visitor switches to a currency the code doesn't support
    useEffect(() => {
        if (appliedCode) {
            if (
                !promoAvailableInCurrency ||
                cartSubtotal < promoRules.minimumSpend ||
                !livePromoConfig.isActive ||
                appliedCode !== livePromoConfig.code
            ) {
                setAppliedCode("");
            }
        }
    }, [cartSubtotal, appliedCode, livePromoConfig, promoRules, promoAvailableInCurrency]);

    // Promo Code Logic (100% Dynamic based on Admin Input)
    const applyPromoCode = (code) => {
        const formattedCode = code.trim().toUpperCase();

        if (!livePromoConfig.isActive) {
            return { success: false, message: "There are no active promotions at this time." };
        }

        if (formattedCode === livePromoConfig.code) {
            if (!promoAvailableInCurrency) {
                return { success: false, message: "This code isn't available for US orders." };
            }
            if (cartSubtotal >= promoRules.minimumSpend) {
                setAppliedCode(formattedCode);
                return { success: true, message: `VIP Discount (${formatPrice(promoRules.discountAmount)} off) applied successfully!` };
            }
            const difference = promoRules.minimumSpend - cartSubtotal;
            return { success: false, message: `Spend ${formatPrice(Math.ceil(difference * 100) / 100)} more to unlock this code.` };
        }

        return { success: false, message: "Invalid promotional code." };
    };

    const removePromoCode = () => {
        setAppliedCode("");
    };

    const addToCart = (product) => {
        const incoming = normaliseStoredItem(product);
        // Don't store the derived fields
        delete incoming.unavailable;

        setCart((prevCart) => {
            const existingItem = prevCart.find((item) => item.id === incoming.id);
            if (existingItem) {
                return prevCart.map((item) =>
                    item.id === incoming.id ? { ...item, quantity: item.quantity + 1 } : item
                );
            }
            return [...prevCart, { ...incoming, quantity: 1 }];
        });
    };

    const removeFromCart = (productId) => {
        setCart((prevCart) => prevCart.filter((item) => item.id !== productId));
    };

    const removeUnavailableItems = () => {
        setCart((prevCart) => prevCart.filter((item) => (currency === "USD" ? item.priceUSD : item.priceGBP) !== null));
    };

    const updateQuantity = (productId, newQuantity) => {
        if (!newQuantity || newQuantity < 1) {
            removeFromCart(productId);
            return;
        }
        setCart((prevCart) =>
            prevCart.map((item) =>
                item.id === productId ? { ...item, quantity: newQuantity } : item
            )
        );
    };

    const getCartItemsCount = () => {
        return cart.reduce((total, item) => total + item.quantity, 0);
    };

    const clearCart = () => {
        setCart([]);
        setAppliedCode("");
    };

    const value = {
        cart: pricedCart,
        addToCart,
        removeFromCart,
        removeUnavailableItems,
        updateQuantity,
        clearCart,
        getCartItemsCount,
        cartSubtotal,
        cartTotal,
        discount,
        appliedCode,
        applyPromoCode,
        removePromoCode,
        hasUnavailableItems,
        unavailableItems,
    };

    return (
        <CartContext.Provider value={value}>
            {children}
        </CartContext.Provider>
    );
};

export default CartContext;